/**
 * @fileoverview Student Portfolio page ("Studio Quiet") — gamified AI portfolio
 * updates, a master overview (instruments, instructors, schools, venues),
 * instrument filtering, manual additions, and a shareable public link.
 *
 * Since C7 (Nov 2026) this is "Share your progress" at /student/progress/share,
 * opened from Progress's Share rather than the sidebar; Back returns to Progress.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import studentApi from "@/lib/studentApi";
import { getErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card, Icon, Button, Avatar } from "@/components/studio/primitives";
import { SHeader } from "@/components/studio/studentUi";
import PortfolioShareCard from "@/components/PortfolioShareCard";

const LEVEL_COLORS = {
  beginner: "bg-[var(--studio-interactive)] text-[var(--text-tertiary)] border-[var(--studio-divider)]",
  developing: "bg-[var(--studio-interactive)] text-[var(--text-secondary)] border-[var(--studio-divider-strong)]",
  proficient: "bg-[var(--state-success-dim)] text-[var(--state-success)] border-[var(--state-success-border)]",
  advanced: "bg-[var(--state-success-dim)] text-[var(--state-success)] border-[var(--state-success-border)]",
  mastered: "bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)] border-[var(--accent-yellow-border)]",
};

const STATUS_COLORS = {
  introduced: "bg-[var(--studio-interactive)] text-[var(--text-tertiary)] border-[var(--studio-divider)]",
  learning: "bg-[var(--studio-interactive)] text-[var(--text-secondary)] border-[var(--studio-divider-strong)]",
  practicing: "bg-[var(--studio-interactive)] text-[var(--text-secondary)] border-[var(--studio-divider-strong)]",
  proficient: "bg-[var(--state-success-dim)] text-[var(--state-success)] border-[var(--state-success-border)]",
  solid: "bg-[var(--state-success-dim)] text-[var(--state-success)] border-[var(--state-success-border)]",
  mastered: "bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)] border-[var(--accent-yellow-border)]",
};

const CATEGORY_ICONS = {
  technique: "zap",
  theory: "brain",
  ear_training: "music",
  performance: "star",
  repertoire: "book",
};

const LOADING_MESSAGES = [
  "Analyzing your lessons…",
  "Mapping your skills…",
  "Identifying your repertoire…",
  "Assessing your growth…",
  "Building your portfolio…",
];

/** Small level/status pill. */
const Pill = ({ label, colorClass }) => (
  <span className={cn("rounded-full border px-2 py-0.5 font-sans text-[10px] font-semibold capitalize", colorClass)}>
    {label}
  </span>
);

const StudentPortfolioPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [interests, setInterests] = useState([]);
  const [newInterest, setNewInterest] = useState("");
  const [manualAdditions, setManualAdditions] = useState({ instruments: [], instructors: [], venues: [], schools: [] });
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const portfolioUrl = status?.account_id ? `${window.location.origin}/portfolio/${status.account_id}` : null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(portfolioUrl);
    setCopied(true);
    toast.success("Portfolio link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await studentApi.get("/student/portfolio-status");
      setStatus(res.data);
      setManualAdditions(res.data.manual_additions || { instruments: [], instructors: [], venues: [], schools: [] });
    } catch (_err) {
      toast.error("Failed to load portfolio status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    studentApi.get("/student/profile")
      .then((res) => { if (res.data.interests) setInterests(res.data.interests); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!updating) return;
    const interval = setInterval(() => setLoadingMsgIdx((prev) => (prev + 1) % LOADING_MESSAGES.length), 2500);
    return () => clearInterval(interval);
  }, [updating]);

  const handleUpdate = async () => {
    setUpdating(true);
    setLoadingMsgIdx(0);
    try {
      await studentApi.post("/student/portfolio/update");
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
      await fetchStatus();
      toast.success("Portfolio updated!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update portfolio"));
    } finally {
      setUpdating(false);
    }
  };

  const saveInterests = async (updated) => {
    try { await studentApi.put("/student/interests", { interests: updated }); }
    catch (_) { toast.error("Failed to save interests"); }
  };

  const addInterest = () => {
    const trimmed = newInterest.trim();
    if (!trimmed || interests.includes(trimmed)) return;
    const updated = [...interests, trimmed];
    setInterests(updated);
    setNewInterest("");
    saveInterests(updated);
  };

  const removeInterest = (interest) => {
    const updated = interests.filter((i) => i !== interest);
    setInterests(updated);
    saveInterests(updated);
  };

  const saveManualAdditions = async (updated) => {
    try {
      await studentApi.put("/student/manual-additions", updated);
      setManualAdditions(updated);
    } catch (_) {
      toast.error("Failed to save");
    }
  };

  const portfolio = status?.portfolio;
  const hasPortfolio = portfolio && portfolio.generation_count > 0;

  const instrumentStats = status?.instrument_stats || {};
  const sortedInstruments = Object.entries(instrumentStats)
    .sort(([, a], [, b]) => b.count - a.count)
    .map(([name, stats]) => ({ name, ...stats, manual: false }));
  (manualAdditions.instruments || []).forEach((mi) => {
    if (!sortedInstruments.find((i) => i.name === mi.name)) {
      sortedInstruments.push({ name: mi.name, count: 0, last_date: null, manual: true, years_played: mi.years_played });
    }
  });

  const filterByInstrument = (items) => {
    if (!selectedInstrument) return items || [];
    return (items || []).filter((item) => item.instrument === selectedInstrument || !item.instrument);
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-[1080px] px-6 pb-32 pt-11 md:px-12">
        <div className="space-y-4">
          <div className="skeleton h-10 w-1/2 rounded-xl" />
          <div className="skeleton h-24 rounded-[20px]" />
          <div className="skeleton h-64 rounded-[20px]" />
        </div>
      </div>
    );
  }

  const allInstructors = [
    ...(status?.teachers || []).map((t) => ({ ...t, manual: false })),
    ...(manualAdditions.instructors || []).map((mi) => ({ ...mi, manual: true })),
  ];
  const hasAboutContent =
    sortedInstruments.length > 0 ||
    allInstructors.length > 0 ||
    (manualAdditions.schools || []).length > 0 ||
    (manualAdditions.venues || []).length > 0 ||
    interests.length > 0;

  return (
    <div className="mx-auto max-w-[1080px] px-6 pb-32 pt-11 md:px-12" data-testid="student-portfolio">
      <button
        type="button"
        onClick={() => navigate("/student/progress")}
        className="-ml-1 mb-4 inline-flex items-center gap-1 rounded-full px-1 py-1 font-sans text-[15px] font-semibold text-[var(--accent-yellow-text)] hover:underline"
        data-testid="share-back"
      >
        <Icon name="chevronLeft" size={18} strokeWidth={2} />
        Progress
      </button>
      <SHeader
        title="Share your progress"
        subtitle="A page for a school, a teacher or family. You choose what it shows."
        action={
          <Button variant={isEditing ? "primary" : "ghost"} icon={isEditing ? "check" : "edit"} onPress={() => setIsEditing(!isEditing)}>
            {isEditing ? "Done" : "Edit"}
          </Button>
        }
      />

      {/* Publishing controls (F8) — above the content, because whether the
          page is public at all is a bigger question than what is on it. */}
      <PortfolioShareCard />

      {/* Update banner */}
      <UpdateBanner status={status} updating={updating} loadingMsgIdx={loadingMsgIdx} showCelebration={showCelebration} onUpdate={handleUpdate} />

      {/* Instrument filter pills */}
      {sortedInstruments.length > 0 && (
        <div className="mb-6 flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FilterChip active={!selectedInstrument} onClick={() => setSelectedInstrument(null)}>All</FilterChip>
          {sortedInstruments.map((inst) => (
            <FilterChip
              key={inst.name}
              active={selectedInstrument === inst.name}
              onClick={() => setSelectedInstrument(inst.name === selectedInstrument ? null : inst.name)}
            >
              {inst.name}
              {inst.count > 0 && <span className="ml-1.5 text-[11px] opacity-50">{inst.count}</span>}
            </FilterChip>
          ))}
          {isEditing && (
            <ManualAddButton
              label="Instrument"
              fields={[{ key: "name", placeholder: "Instrument name" }, { key: "years_played", placeholder: "Years (e.g. 2018-2022)" }]}
              onSave={(item) => saveManualAdditions({ ...manualAdditions, instruments: [...(manualAdditions.instruments || []), item] })}
            />
          )}
        </div>
      )}

      {/* Edit-mode management panels */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            key="edit-panels"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="mb-6 space-y-4"
          >
            {/* Instructors */}
            <Card>
              <PanelHeader icon="user">Instructors</PanelHeader>
              <div className="flex flex-wrap gap-2">
                {(status?.teachers || []).map((t) => (
                  <div key={t.user_id} className="flex items-center gap-2 rounded-[12px] border border-[var(--studio-divider)] bg-[var(--studio-interactive)] px-4 py-2.5">
                    {t.picture ? <img src={t.picture} alt="" className="h-6 w-6 rounded-full" /> : <Avatar name={t.name} size={24} accent />}
                    <span className="font-sans text-sm text-[var(--text-primary)]">{t.name}</span>
                  </div>
                ))}
                {(manualAdditions.instructors || []).map((mi, i) => (
                  <ManualChip
                    key={`m-${i}`}
                    onRemove={() => saveManualAdditions({ ...manualAdditions, instructors: manualAdditions.instructors.filter((_, idx) => idx !== i) })}
                  >
                    <Icon name="user" size={14} className="text-[var(--text-tertiary)]" />
                    <span className="font-sans text-sm text-[var(--text-secondary)]">{mi.name}</span>
                    {mi.instrument && <span className="font-sans text-[11px] text-[var(--text-quaternary)]">({mi.instrument})</span>}
                    {mi.years && <span className="font-sans text-[11px] text-[var(--text-quaternary)]">{mi.years}</span>}
                  </ManualChip>
                ))}
                <ManualAddButton
                  label="Instructor"
                  fields={[
                    { key: "name", placeholder: "Instructor name" },
                    { key: "instrument", placeholder: "Instrument (optional)" },
                    { key: "years", placeholder: "Years (e.g. 2019-2021)" },
                  ]}
                  onSave={(item) => saveManualAdditions({ ...manualAdditions, instructors: [...(manualAdditions.instructors || []), item] })}
                />
              </div>
            </Card>

            {/* Schools & Venues */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card>
                <PanelHeader icon="school">Schools &amp; camps</PanelHeader>
                <div className="flex flex-wrap gap-2">
                  {(manualAdditions.schools || []).map((s, i) => (
                    <ManualChip
                      key={i}
                      onRemove={() => saveManualAdditions({ ...manualAdditions, schools: manualAdditions.schools.filter((_, idx) => idx !== i) })}
                    >
                      <div>
                        <p className="font-sans text-sm text-[var(--text-secondary)]">{s.name}</p>
                        {s.program && <p className="font-sans text-[11px] text-[var(--text-quaternary)]">{s.program}</p>}
                        {s.years && <p className="font-sans text-[11px] text-[var(--text-quaternary)]">{s.years}</p>}
                      </div>
                    </ManualChip>
                  ))}
                  <ManualAddButton
                    label="School"
                    fields={[
                      { key: "name", placeholder: "School or camp name" },
                      { key: "program", placeholder: "Program (optional)" },
                      { key: "years", placeholder: "Years (optional)" },
                    ]}
                    onSave={(item) => saveManualAdditions({ ...manualAdditions, schools: [...(manualAdditions.schools || []), item] })}
                  />
                </div>
              </Card>
              <Card>
                <PanelHeader icon="mapPin">Performance venues</PanelHeader>
                <div className="flex flex-wrap gap-2">
                  {(manualAdditions.venues || []).map((v, i) => (
                    <ManualChip
                      key={i}
                      onRemove={() => saveManualAdditions({ ...manualAdditions, venues: manualAdditions.venues.filter((_, idx) => idx !== i) })}
                    >
                      <div>
                        <p className="font-sans text-sm text-[var(--text-secondary)]">{v.name}</p>
                        {v.event && <p className="font-sans text-[11px] text-[var(--text-quaternary)]">{v.event}</p>}
                      </div>
                    </ManualChip>
                  ))}
                  <ManualAddButton
                    label="Venue"
                    fields={[{ key: "name", placeholder: "Venue name" }, { key: "event", placeholder: "Event (e.g. Spring Recital 2023)" }]}
                    onSave={(item) => saveManualAdditions({ ...manualAdditions, venues: [...(manualAdditions.venues || []), item] })}
                  />
                </div>
              </Card>
            </div>

            {/* Musical interests */}
            <Card>
              <PanelHeader icon="sparkle">Musical interests</PanelHeader>
              <p className="mb-3 font-sans text-[13px] text-[var(--text-tertiary)]">
                Tell Hans what music excites you — your portfolio will highlight relevant skills.
              </p>
              <div className="mb-3 flex flex-wrap gap-2">
                {interests.map((interest) => (
                  <span key={interest} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--studio-divider)] bg-[var(--studio-interactive)] px-3 py-1.5 font-sans text-[13px] text-[var(--text-secondary)]">
                    {interest}
                    <button type="button" onClick={() => removeInterest(interest)} className="hover:text-[var(--text-primary)]" aria-label={`Remove ${interest}`}>
                      <Icon name="x" size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); addInterest(); }} className="flex gap-2">
                <input
                  value={newInterest}
                  onChange={(e) => setNewInterest(e.target.value)}
                  placeholder="Add an interest (e.g., jazz, Beatles, classical)"
                  maxLength={50}
                  className="flex-1 rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] px-3 py-2 font-sans text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-quaternary)] focus:border-[var(--accent-yellow-border-strong)]"
                />
                <button
                  type="submit"
                  disabled={!newInterest.trim()}
                  className="flex items-center justify-center rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:opacity-30"
                  aria-label="Add interest"
                >
                  <Icon name="plus" size={16} />
                </button>
              </form>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter indicator */}
      {selectedInstrument && (
        <div className="mb-4 flex items-center gap-2">
          <span className="font-sans text-[13px] text-[var(--text-tertiary)]">Filtered by:</span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-3 py-1 font-sans text-[13px] text-[var(--accent-yellow-text)]">
            <Icon name="music" size={12} /> {selectedInstrument}
            <button type="button" onClick={() => setSelectedInstrument(null)} aria-label="Clear filter"><Icon name="x" size={12} /></button>
          </span>
        </div>
      )}

      {/* View-mode About section */}
      {!isEditing && hasAboutContent && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 mt-10">
          <div className="mb-5 flex items-center gap-4">
            <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">About</span>
            <div className="h-px flex-1 bg-[var(--studio-divider)]" />
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            {sortedInstruments.length > 0 && (
              <AboutBlock icon="music" label="Instruments">
                {sortedInstruments.map((inst) => (
                  <span key={inst.name} className="rounded-lg bg-[var(--studio-interactive)] px-3 py-1 font-sans text-sm text-[var(--text-secondary)]">
                    {inst.name}
                    {inst.years_played && <span className="ml-1.5 font-sans text-[12px] text-[var(--text-quaternary)]">{inst.years_played}</span>}
                  </span>
                ))}
              </AboutBlock>
            )}
            {allInstructors.length > 0 && (
              <AboutBlock icon="user" label="Instructors">
                {allInstructors.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-2 rounded-lg bg-[var(--studio-interactive)] px-3 py-1.5">
                    {t.picture ? <img src={t.picture} alt="" className="h-5 w-5 rounded-full" /> : <Avatar name={t.name} size={20} accent />}
                    <span className="font-sans text-sm text-[var(--text-secondary)]">{t.name}</span>
                    {t.instrument && <span className="font-sans text-[12px] text-[var(--text-quaternary)]">· {t.instrument}</span>}
                    {t.years && <span className="font-sans text-[12px] text-[var(--text-quaternary)]">{t.years}</span>}
                  </span>
                ))}
              </AboutBlock>
            )}
            {(manualAdditions.schools || []).length > 0 && (
              <AboutBlock icon="school" label="Schools & camps">
                <div className="space-y-1">
                  {manualAdditions.schools.map((s, i) => (
                    <div key={i} className="flex items-baseline gap-2">
                      <span className="font-sans text-sm text-[var(--text-secondary)]">{s.name}</span>
                      {s.program && <span className="font-sans text-[12px] text-[var(--text-quaternary)]">{s.program}</span>}
                      {s.years && <span className="font-sans text-[12px] text-[var(--text-quaternary)]">{s.years}</span>}
                    </div>
                  ))}
                </div>
              </AboutBlock>
            )}
            {(manualAdditions.venues || []).length > 0 && (
              <AboutBlock icon="mapPin" label="Performance venues">
                <div className="space-y-1">
                  {manualAdditions.venues.map((v, i) => (
                    <div key={i} className="flex items-baseline gap-2">
                      <span className="font-sans text-sm text-[var(--text-secondary)]">{v.name}</span>
                      {v.event && <span className="font-sans text-[12px] text-[var(--text-quaternary)]">{v.event}</span>}
                    </div>
                  ))}
                </div>
              </AboutBlock>
            )}
            {interests.length > 0 && (
              <AboutBlock icon="sparkle" label="Musical interests">
                {interests.map((interest) => (
                  <span key={interest} className="rounded-full bg-[var(--studio-interactive)] px-2.5 py-1 font-sans text-[13px] text-[var(--text-secondary)]">{interest}</span>
                ))}
              </AboutBlock>
            )}
          </div>
        </motion.div>
      )}

      {/* AI portfolio display */}
      {hasPortfolio ? <PortfolioDisplay portfolio={portfolio} filterFn={filterByInstrument} /> : <EmptyState status={status} />}

      {/* Share portfolio */}
      {portfolioUrl && (
        <Card className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <Icon name="share" size={16} className="text-[var(--accent-yellow-text)]" />
            <h3 className="font-sans text-sm font-semibold text-[var(--text-primary)]">Your portfolio</h3>
          </div>
          <p className="mb-4 font-sans text-[13px] text-[var(--text-tertiary)]">
            Share your musical journey with teachers, schools, or anyone else. This link shows your lesson history, instruments, and practice stats.
          </p>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 truncate rounded-[10px] bg-[var(--studio-interactive)] px-3 py-2 font-mono text-[12px] text-[var(--text-tertiary)]">
              {portfolioUrl}
            </div>
            <button type="button" onClick={handleCopyLink} aria-label="Copy link" className="rounded-[10px] bg-[var(--studio-interactive)] p-2 text-[var(--text-primary)] transition-colors hover:bg-[var(--studio-overlay)]">
              <Icon name={copied ? "check" : "copy"} size={16} />
            </button>
            <button type="button" onClick={() => window.open(portfolioUrl, "_blank")} aria-label="Open portfolio" className="rounded-[10px] bg-[var(--studio-interactive)] p-2 text-[var(--text-primary)] transition-colors hover:bg-[var(--studio-overlay)]">
              <Icon name="external" size={16} />
            </button>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ─── Small shared bits ─── */

function FilterChip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full border px-4 py-2 font-sans text-sm transition-colors",
        active
          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
          : "border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
      )}
    >
      {children}
    </button>
  );
}

const PanelHeader = ({ icon, children }) => (
  <h2 className="mb-3 flex items-center gap-2 font-sans text-sm font-medium text-[var(--text-primary)]">
    <Icon name={icon} size={16} className="text-[var(--accent-yellow-text)]" /> {children}
  </h2>
);

const ManualChip = ({ children, onRemove }) => (
  <div className="flex items-center gap-2 rounded-[12px] border border-dashed border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2">
    {children}
    <button type="button" onClick={onRemove} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]" aria-label="Remove"><Icon name="x" size={12} /></button>
  </div>
);

const AboutBlock = ({ icon, label, children }) => (
  <div className="flex items-start gap-4">
    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] border border-[var(--studio-divider)] bg-[var(--studio-interactive)]">
      <Icon name={icon} size={18} strokeWidth={1.7} className="text-[var(--text-secondary)]" />
    </div>
    <div className="flex-1">
      <p className="mb-2.5 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  </div>
);

/* ─── Manual add button (inline expandable form) ─── */

const ManualAddButton = ({ label, fields, onSave }) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});

  const handleSave = () => {
    if (!values[fields[0].key]?.trim()) return;
    onSave(values);
    setValues({});
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex shrink-0 items-center gap-1.5 rounded-[12px] border border-dashed border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-secondary)]"
      >
        <Icon name="plus" size={14} /> {label}
      </button>
    );
  }

  return (
    <div className="flex min-w-[200px] shrink-0 flex-col gap-2 rounded-[12px] border border-dashed border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] p-3">
      {fields.map((f) => (
        <input
          key={f.key}
          placeholder={f.placeholder}
          value={values[f.key] || ""}
          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
          maxLength={100}
          className="rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-base)] px-2.5 py-1.5 font-sans text-xs text-[var(--text-primary)] outline-none placeholder:text-[var(--text-quaternary)] focus:border-[var(--accent-yellow-border-strong)]"
        />
      ))}
      <div className="flex gap-1.5">
        <button type="button" onClick={handleSave} className="flex-1 rounded-lg bg-[var(--accent-yellow)] py-1.5 font-sans text-xs font-semibold text-[var(--text-inverse)]">Add</button>
        <button type="button" onClick={() => { setOpen(false); setValues({}); }} className="flex-1 rounded-lg bg-[var(--studio-raised)] py-1.5 font-sans text-xs text-[var(--text-secondary)]">Cancel</button>
      </div>
    </div>
  );
};

/* ─── Update banner ─── */

const UpdateBanner = ({ status, updating, loadingMsgIdx, showCelebration, onUpdate }) => {
  const { pending_count, lessons_required, can_update, pending_lessons, generation_count } = status || {};

  if (updating) {
    return (
      <Card className="mb-6 border-[var(--accent-yellow-border)] text-center">
        <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-[var(--accent-yellow)] border-t-transparent" />
        <AnimatePresence mode="wait">
          <motion.p key={loadingMsgIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="font-sans text-sm font-medium text-[var(--accent-yellow-text)]">
            {LOADING_MESSAGES[loadingMsgIdx]}
          </motion.p>
        </AnimatePresence>
      </Card>
    );
  }

  if (showCelebration) {
    return (
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mb-6 rounded-[20px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-8 text-center">
        <Icon name="trophy" size={48} className="mx-auto mb-3 text-[var(--accent-yellow-text)]" />
        <h3 className="font-serif text-[22px] font-normal text-[var(--text-primary)]">Portfolio updated</h3>
        <p className="mt-1 font-sans text-sm text-[var(--text-tertiary)]">Your musical journey has been refreshed.</p>
      </motion.div>
    );
  }

  if (!pending_count || pending_count === 0) {
    return (
      <Card className="mb-6 flex items-center gap-[18px] border-[var(--state-success-border)]">
        <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-[12px] border border-[var(--state-success-border)] bg-[var(--state-success-dim)]">
          <Icon name="medal" size={20} strokeWidth={1.7} className="text-[var(--state-success)]" />
        </div>
        <div>
          <div className="font-sans text-base font-medium text-[var(--text-primary)]">All caught up</div>
          <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
            {generation_count > 0
              ? "Your portfolio reflects all your lessons. New lessons from your teacher will appear here."
              : "Complete more lessons with your teacher to build your portfolio."}
          </div>
        </div>
      </Card>
    );
  }

  if (can_update) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 rounded-[20px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-faint)] p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 animate-pulse items-center justify-center rounded-full bg-[var(--accent-yellow-dim)]">
              <Icon name="sparkle" size={20} className="text-[var(--accent-yellow-text)]" />
            </div>
            <div>
              <p className="font-sans text-sm font-medium text-[var(--text-primary)]">{pending_count} lesson{pending_count > 1 ? "s" : ""} ready to incorporate</p>
              <p className="font-sans text-[13px] text-[var(--text-tertiary)]">{generation_count === 0 ? "Create your progress portfolio" : "Update your portfolio with new achievements"}</p>
            </div>
          </div>
          <Button icon="sparkle" onPress={onUpdate} className="shrink-0">
            {generation_count === 0 ? "Create" : "Update"}
          </Button>
        </div>
        <div className="space-y-1.5">
          {pending_lessons?.slice(0, 5).map((lesson) => (
            <div key={lesson.lesson_id} className="flex items-center gap-2 font-sans text-[13px] text-[var(--text-tertiary)]">
              <Icon name="chevronRight" size={13} className="text-[var(--accent-yellow-text)]" />
              <span className="text-[var(--text-secondary)]">{lesson.title}</span>
              <span className="text-[var(--text-quaternary)]">· {lesson.teacher_name}</span>
            </div>
          ))}
          {pending_lessons?.length > 5 && <p className="pl-5 font-sans text-[13px] text-[var(--text-tertiary)]">+{pending_lessons.length - 5} more</p>}
        </div>
      </motion.div>
    );
  }

  const progress = (pending_count / lessons_required) * 100;
  return (
    <Card className="mb-6">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--studio-interactive)]">
          <Icon name="target" size={20} className="text-[var(--text-tertiary)]" />
        </div>
        <div className="flex-1">
          <p className="font-sans text-sm font-medium text-[var(--text-primary)]">{pending_count} of {lessons_required} lessons needed</p>
          <p className="font-sans text-[13px] text-[var(--text-tertiary)]">{generation_count === 0 ? "Complete more lessons to create your progress portfolio" : "Keep learning — your next update is almost ready"}</p>
        </div>
      </div>
      <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-[var(--studio-interactive)]">
        <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.8 }} className="h-2 rounded-full bg-[var(--accent-yellow)]" />
      </div>
      <div className="space-y-1.5">
        {pending_lessons?.map((lesson) => (
          <div key={lesson.lesson_id} className="flex items-center gap-2 font-sans text-[13px] text-[var(--text-tertiary)]">
            <Icon name="chevronRight" size={13} className="text-[var(--text-quaternary)]" />
            <span className="text-[var(--text-secondary)]">{lesson.title}</span>
            <span className="text-[var(--text-quaternary)]">· {lesson.teacher_name}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

/* ─── Portfolio display ─── */

const PortfolioSection = ({ icon, title, children, accent = false }) => (
  <Card className={accent ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-faint)]" : ""}>
    <h2 className={cn("mb-3 flex items-center gap-2 font-sans text-sm font-medium", accent ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-primary)]")}>
      <Icon name={icon} size={16} className="text-[var(--accent-yellow-text)]" /> {title}
    </h2>
    {children}
  </Card>
);

const PortfolioDisplay = ({ portfolio, filterFn }) => {
  const skills = filterFn(portfolio.skills);
  const repertoire = filterFn(portfolio.repertoire);
  const concepts = filterFn(portfolio.core_concepts);
  const milestones = filterFn(portfolio.milestones);
  const highlights = portfolio.highlights || [];
  const summary = portfolio.summary || "";

  const skillsByCategory = {};
  skills.forEach((skill) => {
    const cat = skill.category || "other";
    (skillsByCategory[cat] ||= []).push(skill);
  });

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {summary && (
        <PortfolioSection icon="book" title="Musical journey">
          <p className="font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]">{summary}</p>
        </PortfolioSection>
      )}

      {highlights.length > 0 && (
        <PortfolioSection icon="sparkle" title="Highlights" accent>
          <ul className="space-y-2">
            {highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 font-sans text-[15px] text-[var(--text-secondary)]">
                <Icon name="star" size={14} className="mt-1 shrink-0 text-[var(--accent-yellow-text)]" /> {h}
              </li>
            ))}
          </ul>
        </PortfolioSection>
      )}

      {skills.length > 0 && (
        <PortfolioSection icon="zap" title="Skills">
          <div className="space-y-4">
            {Object.entries(skillsByCategory).map(([category, catSkills]) => (
              <div key={category}>
                <div className="mb-2 flex items-center gap-2">
                  <Icon name={CATEGORY_ICONS[category] || "zap"} size={14} className="text-[var(--text-tertiary)]" />
                  <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">{category.replace("_", " ")}</span>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {catSkills.map((skill, i) => (
                    <div key={i} className="rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-interactive)] p-3">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="font-sans text-sm text-[var(--text-primary)]">{skill.name}</span>
                        <Pill label={skill.level} colorClass={LEVEL_COLORS[skill.level] || LEVEL_COLORS.beginner} />
                      </div>
                      {skill.detail && <p className="font-sans text-[13px] text-[var(--text-tertiary)]">{skill.detail}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </PortfolioSection>
      )}

      {repertoire.length > 0 && (
        <PortfolioSection icon="music" title="Repertoire">
          <div className="space-y-2">
            {repertoire.map((piece, i) => (
              <div key={i} className="flex items-center justify-between rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-interactive)] p-3">
                <div>
                  <p className="font-sans text-sm text-[var(--text-primary)]">{piece.title}</p>
                  {piece.composer && <p className="font-sans text-[13px] text-[var(--text-tertiary)]">{piece.composer}</p>}
                </div>
                <Pill label={piece.status} colorClass={STATUS_COLORS[piece.status] || STATUS_COLORS.introduced} />
              </div>
            ))}
          </div>
        </PortfolioSection>
      )}

      {concepts.length > 0 && (
        <PortfolioSection icon="brain" title="Core concepts">
          <div className="space-y-2">
            {concepts.map((concept, i) => (
              <div key={i} className="rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-interactive)] p-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="font-sans text-sm text-[var(--text-primary)]">{concept.name}</span>
                  <Pill label={concept.mastery} colorClass={STATUS_COLORS[concept.mastery] || STATUS_COLORS.introduced} />
                </div>
                <p className="font-sans text-[13px] text-[var(--text-tertiary)]">{concept.description}</p>
              </div>
            ))}
          </div>
        </PortfolioSection>
      )}

      {milestones.length > 0 && (
        <PortfolioSection icon="award" title="Milestones">
          <div className="space-y-3">
            {milestones.map((milestone, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="mt-1.5 h-2 w-2 rounded-full bg-[var(--accent-yellow)]" />
                  {i < milestones.length - 1 && <div className="mt-1 w-px flex-1 bg-[var(--studio-divider-strong)]" />}
                </div>
                <div className="pb-3">
                  <p className="font-sans text-sm text-[var(--text-primary)]">{milestone.title}</p>
                  <p className="font-sans text-[13px] text-[var(--text-tertiary)]">{milestone.description}</p>
                  {milestone.date && (
                    <p className="mt-1 flex items-center gap-1 font-mono text-[12px] text-[var(--text-quaternary)]"><Icon name="clock" size={12} /> {milestone.date}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </PortfolioSection>
      )}
    </motion.div>
  );
};

/* ─── Empty state ─── */

const EmptyState = ({ status }) => {
  const { pending_count, lessons_required } = status || {};
  const needed = (lessons_required || 3) - (pending_count || 0);

  return (
    <Card padding={0}>
      <div className="flex flex-col items-center px-8 py-16 text-center">
        <div className="mb-6 flex h-[84px] w-[84px] items-center justify-center rounded-[22px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]">
          <Icon name="trophy" size={34} strokeWidth={1.5} className="text-[var(--accent-yellow-text)]" />
        </div>
        <h3 className="font-serif text-[30px] font-normal tracking-[-0.01em] text-[var(--text-primary)]">Your portfolio is waiting</h3>
        <p className="mt-3 max-w-[440px] font-sans text-base leading-relaxed text-[var(--text-tertiary)]">
          {needed > 0 ? (
            <>Complete <span className="font-mono text-[var(--accent-yellow-text)]">{needed}</span> more lesson{needed > 1 ? "s" : ""} with your teacher to create your progress portfolio.</>
          ) : (
            "Tap the button above to create your progress portfolio."
          )}
        </p>
        <p className="mt-[18px] max-w-[480px] font-sans text-[13px] leading-relaxed text-[var(--text-quaternary)]">
          Hans will analyze your lessons to build a comprehensive view of your musical skills, repertoire, and growth.
        </p>
      </div>
    </Card>
  );
};

export default StudentPortfolioPage;
